/**
 * Self-hosted SDK value types (migration Part 9).
 *
 * Dependency-free reimplementation of the Sharetribe Flex SDK value types
 * (`UUID`, `Money`, `LatLng`, `LatLngBounds`, `BigDecimal`) with the SAME public
 * shape the SPA relies on (`new Money(amount, currency)`, `.amount`, `.currency`,
 * `new UUID(id)`, `.uuid`, `new LatLng(lat, lng)`, `.lat`/`.lng`, `new UUID`
 * destructured from `sdkTypes`). Instances carry `_sdkType` so existing code that
 * sniffs it keeps working.
 *
 * `replacer`/`reviver` are a self-consistent JSON tag pair used for the SSR
 * preloaded-state boundary (`server/renderer.js` writes with `replacer`,
 * `src/index.js` reads with `reviver`). Because both ends come from the same
 * build (big-bang cutover), the tag format is internal and need not match Flex's
 * transit wire format.
 *
 * This module lets us drop `sharetribe-flex-sdk` from the client type surface;
 * `src/util/sdkLoader.js` re-exports from here.
 */

export class UUID {
  constructor(uuid) {
    this._sdkType = 'UUID';
    this.uuid = uuid;
  }
}

export class LatLng {
  constructor(lat, lng) {
    this._sdkType = 'LatLng';
    this.lat = lat;
    this.lng = lng;
  }
}

export class LatLngBounds {
  constructor(ne, sw) {
    this._sdkType = 'LatLngBounds';
    this.ne = ne;
    this.sw = sw;
  }
}

export class Money {
  constructor(amount, currency) {
    this._sdkType = 'Money';
    this.amount = amount;
    this.currency = currency;
  }
}

export class BigDecimal {
  constructor(value) {
    this._sdkType = 'BigDecimal';
    this.value = value;
  }
}

// Reconstruct a class instance from a tagged plain object (used by `reviver`).
const fromTagged = value => {
  switch (value._sdkType) {
    case 'UUID':
      return new UUID(value.uuid);
    case 'LatLng':
      return new LatLng(value.lat, value.lng);
    case 'LatLngBounds':
      return new LatLngBounds(value.ne, value.sw);
    case 'Money':
      return new Money(value.amount, value.currency);
    case 'BigDecimal':
      return new BigDecimal(value.value);
    default:
      return value;
  }
};

// JSON.stringify replacer. Instances already serialize to `{ _sdkType, ... }`
// via their own enumerable props, so this is effectively identity; kept as a
// function to preserve the `server/renderer.js` call signature and to strip
// nothing else.
export const replacer = (key = null, value) => value;

// Sharetribe date attributes: the Flex SDK deserialized these into Date objects,
// so the SPA reads them as Dates (e.g. `lastTransitionedAt.getTime()` in the
// CheckoutPage reducer, `formatDate`, booking ranges). Our self-hosted API emits
// them as ISO strings, so revive the known date keys back into Date instances.
// Scoped to these keys (not a blanket ISO-string match) so ISO-looking strings
// inside user-controlled publicData/metadata are left untouched.
const DATE_KEYS = new Set([
  'createdAt',
  'lastTransitionedAt',
  'deletedAt',
  'expiresAt',
  'at',
  'start',
  'end',
  'displayStart',
  'displayEnd',
]);
// Strict ISO-8601 datetime (matches Date.prototype.toISOString + common offsets).
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

// JSON.parse reviver: reconstruct SDK value instances from their tags. Runs
// bottom-up, so nested types (e.g. LatLngBounds -> LatLng) are rebuilt first.
export const reviver = (key, value) => {
  if (value && typeof value === 'object' && value._sdkType) {
    return fromTagged(value);
  }
  if (typeof value === 'string' && DATE_KEYS.has(key) && ISO_DATETIME_RE.test(value)) {
    return new Date(value);
  }
  return value;
};

export const types = { UUID, LatLng, LatLngBounds, Money, BigDecimal, replacer, reviver };
